import React, { useState } from 'react';
import crmClient from './crmClient';

const AUDIENCE_LABELS = { customers: 'Customers', drivers: 'Drivers', investors: 'Investors' };

const AIAssistantView = ({ meta }) => {
  const audiences = (meta && meta.ai_audiences) || ['customers', 'drivers', 'investors'];
  const tones = (meta && meta.ai_tones) || ['professional', 'friendly', 'concise', 'excited'];

  const [audience, setAudience] = useState(audiences[0]);
  const [tone, setTone] = useState(tones[0]);
  const [topic, setTopic] = useState('');
  const [highlights, setHighlights] = useState('');
  const [draft, setDraft] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [source, setSource] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [savedNote, setSavedNote] = useState(null);

  const generate = () => {
    setBusy(true);
    setError(null);
    setSavedNote(null);
    crmClient
      .aiDraft({
        audience,
        tone,
        topic,
        highlights: highlights
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean)
      })
      .then(payload => {
        setBusy(false);
        setDraft((payload && payload.draft) || null);
        setMetrics((payload && payload.metrics) || null);
        setSource((payload && payload.source) || null);
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to generate a draft.');
      });
  };

  const saveAsTemplate = () => {
    if (!draft) {
      return;
    }
    setBusy(true);
    setError(null);
    crmClient
      .createTemplate({
        name: `${AUDIENCE_LABELS[audience] || audience} — ${topic || 'AI draft'}`.slice(0, 120),
        subject: draft.subject,
        body: draft.body,
        category: 'marketing',
        audience
      })
      .then(() => {
        setBusy(false);
        setSavedNote('Saved to Templates. You can now use it in a campaign.');
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to save template.');
      });
  };

  const updateDraft = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  return (
    <div className="crm-ai">
      <div className="crm-ai-grid">
        <div className="crm-card">
          <h3>Draft with Gophr AI</h3>
          <p className="crm-muted">Generate a campaign draft from approved Gophr data. Drafts require your review before sending.</p>

          <div className="crm-field-row">
            <label>
              <span>Audience</span>
              <select value={audience} onChange={event => setAudience(event.target.value)}>
                {audiences.map(item => (
                  <option key={item} value={item}>
                    {AUDIENCE_LABELS[item] || item}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Tone</span>
              <select value={tone} onChange={event => setTone(event.target.value)}>
                {tones.map(item => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="crm-ai-field">
            <span>Topic</span>
            <input type="text" value={topic} onChange={event => setTopic(event.target.value)} placeholder="e.g. New same-day delivery in Lake Charles" />
          </label>

          <label className="crm-ai-field">
            <span>Highlights (one per line)</span>
            <textarea
              rows={5}
              value={highlights}
              onChange={event => setHighlights(event.target.value)}
              placeholder={'Faster driver matching\nNew market: Beaumont\nOn-time rate up to 98%'}
            />
          </label>

          <button type="button" className="crm-btn" onClick={generate} disabled={busy}>
            {busy ? 'Generating…' : 'Generate draft'}
          </button>

          {error && <div className="crm-inline-error" style={{ marginTop: 12 }}>{error}</div>}
        </div>

        <div className="crm-card">
          <h3>Draft preview</h3>
          {!draft && <p className="crm-muted">Your generated draft will appear here for review and editing.</p>}
          {draft && (
            <>
              {source && (
                <div className={`crm-ai-source crm-ai-source--${source}`}>
                  {source === 'ai' ? 'Generated by AI model' : 'Generated from approved template'}
                </div>
              )}
              <label className="crm-ai-field">
                <span>Subject</span>
                <input type="text" value={draft.subject} onChange={event => updateDraft('subject', event.target.value)} />
              </label>
              <label className="crm-ai-field">
                <span>Body</span>
                <textarea rows={12} value={draft.body} onChange={event => updateDraft('body', event.target.value)} />
              </label>
              <div className="crm-detail-actions">
                <button type="button" className="crm-btn" onClick={saveAsTemplate} disabled={busy}>
                  Save as template
                </button>
                <button
                  type="button"
                  className="crm-btn crm-btn--ghost"
                  onClick={() => navigator.clipboard && navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`)}
                >
                  Copy
                </button>
              </div>
              {savedNote && <div className="crm-muted" style={{ marginTop: 8 }}>{savedNote}</div>}
              {metrics && (
                <div className="crm-ai-metrics">
                  <span>Grounded in: {Number(metrics.deliveries_last_30).toLocaleString('en-US')} deliveries (30d)</span>
                  <span>{Number(metrics.active_accounts).toLocaleString('en-US')} active accounts</span>
                  <span>{Number(metrics.new_accounts_30).toLocaleString('en-US')} new accounts (30d)</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAssistantView;
